import { useEffect, useMemo, useState } from 'react';
import { MenuList } from '../jsx/layouts/nav/Menu';
import { getModuleEntryPath, getSettingsEntryPath } from '../jsx/layouts/nav/moduleNav';
import { accessControlService } from '../services/accessControlService';
import { hasFeatureAccess } from '../utils/featureLock';
import { hasDocumentSubscription } from '../utils/documentAccess';
import { hasForumSubscription } from '../utils/forumAccess';
import { hasInvoiceScannerSubscription } from '../utils/invoiceScannerAccess';
import { getCurrentUserRole } from '../utils/roleAccess';
import {
  getVisibleMenuContent,
  isModuleSubscriptionLocked,
  isSubItemFeatureLocked,
  getModuleLockedRedirect,
} from '../utils/moduleMenuAccess';

const SETTINGS_TITLE = 'Settings';

function shortLabel(title) {
  if (title === 'Discussion Forum') return 'Forum';
  if (title === 'Credentials Vault') return 'Vault';
  return title;
}

function parsePlanFeatures() {
  try {
    const featuresStr = localStorage.getItem('userPlanFeatures');
    if (!featuresStr) return [];
    const parsed = JSON.parse(featuresStr);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((f) => {
        if (typeof f === 'string') return f;
        if (f?.feature) return f.feature;
        if (f?.feature_key) return f.feature_key;
        if (f?.key) return f.key;
        return null;
      })
      .filter(Boolean);
  } catch {
    return [];
  }
}

/**
 * Module launcher tiles filtered by Access Control (corporate ∩ user) and subscription locks.
 * Icons come from MenuList / caller — this hook returns plain tile descriptors.
 */
export function useAccessibleModuleTiles(enabled = true) {
  const [menuAccessKeys, setMenuAccessKeys] = useState(null);
  const [menuAccessRoutePaths, setMenuAccessRoutePaths] = useState(null);
  const [menuAccessFetchResolved, setMenuAccessFetchResolved] = useState(false);
  const [userRole, setUserRole] = useState(() => getCurrentUserRole());
  const [locks, setLocks] = useState({
    documents: false,
    forum: false,
    invoiceScanner: false,
    project: false,
    branch: false,
    department: false,
    worksheet: false,
    planFeatures: parsePlanFeatures(),
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!enabled) return undefined;

    let cancelled = false;

    const loadAccess = async () => {
      setUserRole(getCurrentUserRole());
      try {
        const res = await accessControlService.getMyAccess();
        if (cancelled) return;
        if (res?.data?.success) {
          const keys = res.data.data?.menuKeys;
          setMenuAccessKeys(Array.isArray(keys) ? keys : []);
          const rp = res.data.data?.routePaths;
          setMenuAccessRoutePaths(Array.isArray(rp) ? rp : null);
        } else {
          setMenuAccessKeys(null);
          setMenuAccessRoutePaths(null);
        }
      } catch {
        if (!cancelled) {
          setMenuAccessKeys(null);
          setMenuAccessRoutePaths(null);
        }
      } finally {
        if (!cancelled) setMenuAccessFetchResolved(true);
      }
    };

    const loadLocks = async () => {
      try {
        const [
          documents,
          forum,
          invoiceScanner,
          project,
          branch,
          department,
          worksheet,
        ] = await Promise.all([
          hasDocumentSubscription(),
          hasForumSubscription(),
          hasInvoiceScannerSubscription(),
          hasFeatureAccess('project'),
          hasFeatureAccess('branch'),
          hasFeatureAccess('department'),
          hasFeatureAccess('worksheet'),
        ]);
        if (cancelled) return;
        setLocks({
          documents,
          forum,
          invoiceScanner,
          project,
          branch,
          department,
          worksheet,
          planFeatures: parsePlanFeatures(),
        });
      } catch {
        if (!cancelled) {
          setLocks((prev) => ({
            ...prev,
            documents: false,
            forum: false,
            invoiceScanner: false,
            project: false,
            branch: false,
            department: false,
            worksheet: false,
            planFeatures: parsePlanFeatures(),
          }));
        }
      }
    };

    const load = async () => {
      setLoading(true);
      await Promise.all([loadAccess(), loadLocks()]);
      if (!cancelled) setLoading(false);
    };

    load();

    const refresh = () => {
      loadAccess();
      loadLocks();
    };

    window.addEventListener('menu-access-updated', refresh);
    window.addEventListener('user-subscription-updated', refresh);
    window.addEventListener('user-logged-in', refresh);

    return () => {
      cancelled = true;
      window.removeEventListener('menu-access-updated', refresh);
      window.removeEventListener('user-subscription-updated', refresh);
      window.removeEventListener('user-logged-in', refresh);
    };
  }, [enabled]);

  const accessCtx = useMemo(
    () => ({
      menuAccessKeys,
      menuAccessRoutePaths,
      menuAccessFetchResolved,
      userRole,
    }),
    [menuAccessKeys, menuAccessRoutePaths, menuAccessFetchResolved, userRole]
  );

  const tiles = useMemo(() => {
    if (!menuAccessFetchResolved) return [];

    const result = [
      {
        key: 'dashboard',
        title: 'Dashboard',
        label: 'Dashboard',
        to: '/dashboard',
        iconClass: 'fas fa-th-large',
        locked: false,
      },
    ];

    MenuList.forEach((module) => {
      if (module.title === SETTINGS_TITLE) {
        result.push({
          key: module.menu_key || module.title,
          title: module.title,
          label: shortLabel(module.title),
          to: getSettingsEntryPath(),
          iconStyle: module.iconStyle,
          iconClass: 'fas fa-cog',
          locked: false,
          isSettings: true,
        });
        return;
      }

      const visibleContent = getVisibleMenuContent(module, accessCtx);
      if (module.content?.length > 0 && visibleContent.length === 0) return;

      const moduleLocked = isModuleSubscriptionLocked(module, locks);
      const entryItem = visibleContent.find((item) => item.to && item.to !== '#');
      const entryLocked =
        moduleLocked ||
        (entryItem ? isSubItemFeatureLocked(entryItem, moduleLocked, locks) : false);
      const entryPath = getModuleEntryPath(module, visibleContent);
      const to =
        entryLocked && entryItem
          ? getModuleLockedRedirect(entryItem, module)
          : entryPath || '#';

      if (!to || to === '#') return;

      result.push({
        key: module.menu_key || module.title,
        title: module.title,
        label: shortLabel(module.title),
        to,
        iconStyle: module.iconStyle,
        iconClass: 'fas fa-cube',
        locked: entryLocked,
        isSettings: false,
      });
    });

    return result;
  }, [accessCtx, locks, menuAccessFetchResolved]);

  return { tiles, loading };
}
